import { type ChangeEvent, type KeyboardEvent } from "react";
import { useController, useFormContext, useWatch } from "react-hook-form";

import { Box } from "ui/box";
import { IconButton } from "ui/icon-button";
import { TextField } from "ui/text-field";

import { useAdobeProjectId } from "containers/admin/clients/client/theme-configuration-page/brand-identity/fonts-tab/use-adobe-project-id";

import type { BrandIdentityFormValues } from "containers/admin/clients/client/theme-configuration-page/brand-identity/brand-identity.types";

import { FIELD_ROW_SX, FIELD_SX } from "containers/admin/clients/client/theme-configuration-page/brand-identity/fonts-tab/fonts-tab.styles";

import Icons from "assets";

type AdobeFontsFieldProps = {
  isEditing: boolean;
};

export const AdobeFontsField = ({ isEditing }: AdobeFontsFieldProps) => {
  const form = useFormContext<BrandIdentityFormValues>();
  const { applyDraft, isApplying } = useAdobeProjectId(form);
  const { field, fieldState } = useController<BrandIdentityFormValues, "fonts.adobeFontsDraft">({
    name: "fonts.adobeFontsDraft",
  });
  const appliedProjectId = useWatch<BrandIdentityFormValues, "fonts.adobeFontsProjectId">({
    name: "fonts.adobeFontsProjectId",
  });

  const trimmedDraft = field.value.trim();
  const isApplyDisabled = !trimmedDraft || trimmedDraft === appliedProjectId || isApplying;

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    form.clearErrors(field.name);
    field.onChange(event.target.value);
  };

  const handleClear = () => {
    form.clearErrors(field.name);
    field.onChange("");
    form.setValue("fonts.adobeFontsProjectId", "", { shouldDirty: true });
  };

  // The field sits inside the Brand identity form, where Enter would otherwise submit Save.
  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      void applyDraft();
    }
  };

  const handleApplyClick = () => {
    void applyDraft();
  };

  return (
    <Box sx={FIELD_ROW_SX}>
      <TextField
        label="Adobe fonts Project ID"
        name={field.name}
        ref={field.ref}
        value={field.value}
        onChange={handleChange}
        onBlur={field.onBlur}
        onKeyDown={handleKeyDown}
        onClear={isEditing ? handleClear : undefined}
        disabled={!isEditing}
        errMessage={fieldState.error?.message}
        sx={FIELD_SX}
      />
      {isEditing && (
        <IconButton
          aria-label="Apply Adobe fonts Project ID"
          variant="text"
          size="large"
          disabled={isApplyDisabled}
          onClick={handleApplyClick}>
          <Icons.Checkmark />
        </IconButton>
      )}
    </Box>
  );
};
